import { motion, useInView } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

function TerminalReadoutLine({ label, value, status, color, delay = 0, active }: { label: string; value: string; status: string; color: string; delay?: number; active: boolean }) {
  const [displayed, setDisplayed] = useState('');
  const [started, setStarted] = useState(false);
  const fullText = `> ${label}: ${value} [${status}]`;

  useEffect(() => {
    if (!active) return undefined;
    const timer = setTimeout(() => setStarted(true), delay * 1000);
    return () => clearTimeout(timer);
  }, [active, delay]);

  useEffect(() => {
    if (!started) return undefined;
    let i = 0;
    const interval = setInterval(() => {
      i += 1;
      setDisplayed(fullText.slice(0, i));
      if (i >= fullText.length) clearInterval(interval);
    }, 25);
    return () => clearInterval(interval);
  }, [started, fullText]);

  return (
    <div className='py-0.5 font-mono text-[11px] leading-relaxed md:text-xs' style={{ fontFamily: 'var(--font-jetbrains), monospace', color, minHeight: '1.4em' }}>
      {displayed}
      {started && displayed.length < fullText.length && (
        <motion.span animate={{ opacity: [1, 0] }} transition={{ duration: 0.4, repeat: Infinity }} style={{ color }}>
          _
        </motion.span>
      )}
    </div>
  );
}

export function TerminalReadout({ lines, color, delay = 0 }: { lines: readonly { label: string; value: string; status: string }[]; color: string; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <div ref={ref} className='mt-4 rounded-lg border p-3 md:p-4' style={{ backgroundColor: `${color}08`, borderColor: `${color}25` }}>
      {lines.map((line, i) => (
        <TerminalReadoutLine key={line.label} label={line.label} value={line.value} status={line.status} color={color} delay={delay + i * 0.12} active={inView} />
      ))}
    </div>
  );
}
